<template>
  <v-sheet class="my-3 mx-auto" rounded outlined :width="size">
    <!-- signin -->
    <v-col cols="12" v-if="view === 'signin'">
      <v-card class="my-16 mx-auto" width="400" flat>
        <v-card-text class="py-4 px-3">
          <span class="d-block mb-3 body-1 font-weight-medium">Correo electrónico</span>
          <v-text-field placeholder="example@email.com" background-color="white" height="36" outlined required single-line dense hide-details />
        </v-card-text>
        <v-divider class="mx-3" />
        <v-card-actions class="py-4 px-3">
          <v-btn block color="primary" :ripple="false">Solicitar acceso</v-btn>
        </v-card-actions>
      </v-card>
    </v-col>
    <!-- end signin -->

    <!-- main -->
    <v-col cols="12" v-else>
      <v-card flat>
        <!-- billing information -->
        <v-card-text>
          <v-row no-gutters>
            <span class="my-2 subtitle-1 font-weight-medium">Información de facturación</span>
            <v-sheet outlined rounded width="100%">
              <v-row class="px-4" align="center" justify="start">
                <v-col :cols="small ? 12 : 6">
                  <span class="d-block text--secondary font-weight-medium">{{billing.name}}</span>
                  <span class="d-block">{{billing.tax_id}}</span>
                  <span class="d-block">{{billing.address}} - {{billing.state}}, {{billing.country}}</span>
                  <span class="d-block">{{billing.email}}</span>
                  <span class="d-block">{{billing.phone}}</span>
                </v-col>
                <v-fade-transition v-if="manageBilling">
                  <v-col :class="{ 'text-right': !small }">
                    <v-btn outlined :ripple="false">Editar</v-btn>
                  </v-col>
                </v-fade-transition>
              </v-row>
            </v-sheet>
          </v-row>
        </v-card-text>
        <!-- end billing information -->

        <!-- subscription -->
        <v-card-text>
          <template v-for="subscription in subscriptions">
            <v-row no-gutters :key="`${subscription.id}-overview`">
              <v-col cols="12" class="pb-2">
                <span class="mr-1 subtitle-1 font-weight-medium">Resumen de la suscripción</span>
                <v-chip class="mt-n1" v-if="subscription.status === 'active'" color="blue accent-4" text-color="white" small>Activo</v-chip>
                <v-chip class="mt-n1" v-else-if="subscription.status === 'paused'" color="blue-grey darken-1" text-color="white" small>Pausado</v-chip>
                <v-chip class="mt-n1" v-else-if="subscription.status === 'cancelled'" color="blue-grey darken-1" text-color="white" small>Cancelada</v-chip>
              </v-col>
              <v-col cols="12">
                <v-card outlined>
                  <v-card-text>
                    <v-row align="center" justify="start">
                      <v-col :cols="small ? 12 : 3">
                        <span class="d-block mb-1 text--primary">Inicio</span>
                        <span class="body-1 font-weight-medium mr-1">{{subscription.date_start | date}}</span>
                      </v-col>
                      <v-divider class="my-2" vertical />
                      <v-col :cols="small ? 12 : 3">
                        <span class="d-block mb-1 text--primary">Próxima facturación</span>
                        <span class="body-1 font-weight-medium mr-1">{{subscription.next_billing | date}}</span>
                      </v-col>
                      <v-fade-transition v-if="cancelSubscription">
                        <v-col :class="{ 'text-right': !small }">
                          <v-btn outlined :ripple="false">Cancelar suscripción</v-btn>
                        </v-col>
                      </v-fade-transition>
                    </v-row>
                  </v-card-text>
                  <v-divider />
                  <v-card-text>
                    <v-row no-gutters align="center" justify="space-between" v-for="(line, i) in subscription.lines" :key="`${i}-line`">
                      <v-col class="py-3" :cols="small ? 12 : 6">
                        <span class="d-block body-2 font-weight-medium mr-1">
                          {{line.plan.product}} • {{line.plan.name}}
                          <v-tooltip v-if="line.plan.usage_scheme === 'metered'" right max-width="280px" transition="scale-transition">
                            <template v-slot:activator="{ on }">
                              <v-icon class="ml-1" v-on="on" small :color="account.color_background">mdi-timelapse</v-icon>
                            </template>
                            <span>Este plan se factura de acuerdo al consumo registrado en cada periodo de facturación.</span>
                          </v-tooltip>
                        </span>
                        <span class="d-block text--primary subtitle mr-1">
                          <template v-if="line.plan.tiers.length">Desde</template>
                          {{line.plan.amount | currency(line.plan.currency.id)}} {{line.plan.currency.id}} por {{line.plan.frequency.name}}
                          <template v-if="line.plan.frequency === 'monthly'">mes</template>
                          <template v-else-if="line.plan.frequency === 'annual'">año</template>
                        </span>
                      </v-col>
                      <v-col class="py-3" :class="{ 'text-right': !small }" :cols="small ? 12 : 3">
                        <span v-if="line.plan.usage_scheme === 'licensed'" class="body-2 text--secondary font-weight-medium">Cant. {{line.quantity}}</span>
                        <span v-else-if="line.plan.usage_scheme === 'metered'">Varía con el uso</span>
                      </v-col>
                      <v-col class="py-3" :class="{ 'text-right': !small }" :cols="small ? 12 : 3">
                        <template v-if="line.plan.usage_scheme === 'licensed'">
                          <span class="body-1 text--secondary font-weight-medium">{{(line.quantity * line.plan.amount) | currency(line.plan.currency.id)}}</span> <span class="text--primary">{{line.plan.currency.id}}</span>
                        </template>
                        <span v-else-if="line.plan.usage_scheme === 'metered'">Varía con el uso</span>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </template>
        </v-card-text>
        <!-- end subscription -->

        <!-- sources -->
        <v-card-text>
          <v-row no-gutters>
            <v-col cols="12" class="pb-2">
              <span class="subtitle-1 font-weight-medium">Métodos de pago</span>
            </v-col>
            <v-sheet outlined rounded width="100%">
              <v-row class="px-4" align="center" justify="start" v-for="s in sources" :key="s.id">
                <v-col :cols="small ? 12 : 6">
                  <v-list-item dense>
                    <v-list-item-icon :rounded="false">
                      <card-brand :brand="s.card.brand" />
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        <span class="body-2 text--secondary font-weight-medium">•••• •••• •••• {{s.card.last4}}</span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-col>
                <v-col :cols="small ? 12 : 6">
                  <v-list-item dense>
                    <v-list-item-content>
                      <v-list-item-title>
                        <span class="text--primary font-weight-regular">pago procesado vía</span>
                      </v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-icon :rounded="false">
                      <img :src="require(`@/assets/appAsset-brand--${s.gateway}.svg`)" :alt="s.gateway" height="24" />
                    </v-list-item-icon>
                  </v-list-item>
                </v-col>
              </v-row>
              <v-fade-transition group>
                <template v-if="manageSource">
                  <v-divider key="transition-group-divider" />
                  <v-row class="px-7 py-3" :justify="small ? 'start': 'end'" key="transition-group-actions">
                    <v-btn :color="account.color_background" dark :ripple="false">Agregar tarjeta</v-btn>
                  </v-row>
                </template>
              </v-fade-transition>
            </v-sheet>
          </v-row>
        </v-card-text>
        <!-- end sources -->

        <!-- invoices -->
        <v-fade-transition>
          <v-card-text v-if="displayInvoices">
            <v-row no-gutters>
              <v-col cols="12" class="pb-2">
                <span class="subtitle-1 font-weight-medium">Facturas</span>
              </v-col>
              <v-col cols="12">
                <v-card class="mx-0" outlined>
                  <v-data-table class="transparent" :headers="headers" :items="invoices" mobile-breakpoint hide-default-footer hide-default-header disable-sort>
                    <template v-slot:[`item.document`]="{item}">
                      <v-list-item class="px-0">
                        <v-list-item-title>
                          <span class="d-block mr-1 body-2">{{item.document.name}}</span>
                          <span class="d-block mr-1 body-2 text--secondary font-weight-medium">Nº {{item.number}}</span>
                        </v-list-item-title>
                        <v-list-item-action class="mx-0">
                          <v-btn text :color="account.color_background" :ripple="false">PDF<v-icon class="mt-1" small right>mdi-arrow-down</v-icon></v-btn>
                        </v-list-item-action>
                      </v-list-item>
                    </template>
                    <template v-slot:[`item.status`]="{item}">
                      <v-row align="center" justify="end">
                        <v-chip v-if="item.status === 'finalized'" class="mx-1" color="blue-grey darken-1" text-color="white" x-small>Pendiente de pago</v-chip>
                        <v-chip v-else-if="item.status === 'paid'" class="mx-1" color="blue lighten-5" text-color="blue accent-4" x-small>Pagado</v-chip>
                      </v-row>
                    </template>
                    <template v-slot:[`item.due_date`]="{item}">
                      <span class="body-2 text--primary" v-if="item.due_date">{{item.due_date | date}}</span>
                      <span v-else>---</span>
                    </template>
                    <template v-slot:[`item.total`]="{item}">
                      <div class="text-right">
                        <span class="body-1 text--secondary font-weight-medium">{{item.total | currency(item.currency)}}</span>
                        <span class="ml-1 text--primary">{{item.currency}}</span>
                      </div>
                    </template>
                    <template v-slot:[`item.actions`]="{item}">
                      <div class="text-right">
                        <template v-if="item.status === 'finalized'">
                          <v-btn text :color="account.color_background" :ripple="false">Pagar<v-icon small right>mdi-arrow-right</v-icon></v-btn>
                        </template>
                      </div>
                    </template>
                  </v-data-table>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-fade-transition>
        <!-- end invoices -->

        <v-card-text>
          <v-row class="px-2" align="center" justify="end" no-gutters>
            <span class="caption text--primary">con la tecnología de</span> <img class="ml-1" :src="require(`@/assets/appLogo-color--Onlight.svg`)" height="16px">
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>
    <!-- end main -->
  </v-sheet>
</template>
<script>
import GenericViewMixin from '@/mixins/GenericViewMixin'
import CardBrand from '@/components/payments/CardBrand'

export default {
  components: {
    CardBrand
  },
  props: {
    cancelSubscription: {
      type: Boolean,
      required: false,
      default: () => (true)
    },
    displayInvoices: {
      type: Boolean,
      required: false,
      default: () => (true)
    },
    manageSource: {
      type: Boolean,
      required: false,
      default: () => (true)
    },
    size: {
      type: Number,
      required: false,
      default () {
        return 700
      }
    },
    manageBilling: {
      type: Boolean,
      required: false,
      default: () => (true)
    },
    view: {
      type: String,
      required: false,
      default () {
        return 'main'
      },
      validator (val) {
        return ['main', 'signin'].includes(val)
      }
    }
  },
  mixins: [
    GenericViewMixin
  ],
  data: () => ({
    billing: {
      address: '599 Flannery St, Santa Clara',
      country: 'US',
      email: 'foo@example.com',
      name: 'Acme',
      phone: '+1 408 827 6068',
      state: 'CA',
      tax_id: '99-9237865'
    },
    headers: [
      { value: 'document', text: 'Documento' },
      { value: 'status', text: '' },
      { value: 'due_date', text: 'Vencimiento', align: 'end', sortable: true },
      { value: 'total', text: 'Total', align: 'end', sortable: true },
      { value: 'actions', text: 'Total', align: 'end', sortable: true }
    ],
    subscriptions: [{
      id: 'sub_xxxxxxxxxxxxx',
      date_start: '2023-05-26',
      lines: [
        {
          plan: {
            currency: {
              id: 'USD',
              rounded: false
            },
            product: 'Plural Soft',
            tiers: [],
            sku: null,
            name: 'Plan estándar',
            amount: 186.6,
            tiers_mode: null,
            exempt: false,
            usage_scheme: 'licensed',
            usage_aggregation: null,
            status: 'active',
            unit_label: null,
            metadata: {},
            frequency: 'monthly'
          },
          quantity: 1
        }
      ],
      next_billing: '2023-08-26',
      status: 'active'
    }],
    invoices: [
      {
        currency: 'USD',
        date: '2023-08-26',
        document: {
          name: 'Recibo'
        },
        due_date: '2023-08-26',
        number: 49569,
        status: 'finalized',
        total: 186.6
      },
      {
        currency: 'USD',
        date: '2023-07-16',
        document: {
          name: 'Recibo'
        },
        due_date: '2023-07-26',
        number: 48512,
        status: 'paid',
        total: 186.6
      },
      {
        currency: 'USD',
        date: '2023-06-16',
        document: {
          name: 'Recibo'
        },
        due_date: '2023-06-26',
        number: 46814,
        status: 'paid',
        total: 186.6
      },
      {
        currency: 'USD',
        date: '2023-05-16',
        document: {
          name: 'Recibo'
        },
        due_date: '2023-05-26',
        number: 45271,
        status: 'paid',
        total: 186.6
      }
    ],
    sources: [
      {
        card: {
          brand: 'Visa',
          exp_month: null,
          exp_year: null,
          last4: '6623'
        },
        gateway: 'stripe',
        status: 'chargeable'
      }
    ]
  }),
  computed: {
    small () {
      return (this.size <= 600)
    }
  }
}
</script>